import axios from 'axios';

/**
 * Same-origin-aware API base URL.
 *
 * Why this is more resilient than just using `REACT_APP_BACKEND_URL`:
 *   • Production deployments serve frontend and backend from the SAME domain
 *     (e.g. https://crm.mindcareindia.com  → /api/* routes to FastAPI,
 *     everything else routes to React). So a relative `/api` path always
 *     reaches the correct backend, regardless of what env var was baked into
 *     the bundle at build time.
 *   • In the preview / dev sandbox the frontend and backend live on different
 *     hosts, so we honour `REACT_APP_BACKEND_URL` there.
 *
 * Logic:
 *   1. If REACT_APP_BACKEND_URL is set AND its origin equals the current
 *      window.location.origin (preview/dev), use it explicitly.
 *   2. Otherwise (production, or stale env baked into bundle), use a relative
 *      `/api` path — the browser sends it to the same domain serving the page.
 */
function resolveApiBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') {
    // SSR / build time — return what env says
    return envUrl ? `${envUrl.replace(/\/$/, '')}/api` : '/api';
  }
  if (envUrl) {
    try {
      const envOrigin = new URL(envUrl).origin;
      if (envOrigin === window.location.origin) {
        return `${envUrl.replace(/\/$/, '')}/api`;
      }
    } catch {
      // invalid URL — fall through to relative path
    }
  }
  return '/api';
}

const api = axios.create({
  baseURL: resolveApiBase(),
  // No cookies — auth is via Bearer token in localStorage (see interceptor below).
  // This also avoids the "wildcard CORS + credentials" browser block.
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

// Attach the bearer token from localStorage on every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default api;
